// English dictionary — professional marketing-consulting translation.
export const en = {
  meta: {
    htmlLang: "en",
    ogLocale: "en_US",
    ogLocaleAlternate: "fr_FR",
    dateLocale: "en-GB",
  },
  zones: "Nice · Sophia Antipolis · Cannes · Antibes · Monaco · French Riviera · France & international (remote)",
  role: "Strategic marketing consultant",
  logoAlt: "Bouge Ton Marketing logo — brand and marketing strategy consultant for SMEs",
  nav: {
    mainAria: "Main navigation",
    mobileAria: "Mobile navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    homeAria: "Magali Clouzet — Home",
    tagline: "Strategic Marketing",
    bookCall: "Book a call",
    links: [
      { label: "Home", path: "/" },
      { label: "About", path: "/qui-suis-je" },
      { label: "Services", path: "/services" },
      { label: "Blog", path: "/blog" },
      { label: "FAQ", path: "/faq" },
      { label: "Contact", path: "/contact" },
    ],
  },
  common: {
    home: "Home",
    skipLink: "Skip to main content",
    breadcrumbAria: "Breadcrumb",
    offerBadge: "Offer",
    discoverOffer: "Discover this offer",
    requestQuote: "Request a quote",
    bookCall: "Book a call",
    readSuffix: "read",
    cta: {
      title: "Let's discuss your project",
      text: "An initial 30-minute call — free, zero commitment. We'll discuss your challenges, and you'll leave with a clear roadmap for next steps.",
      ctaLabel: "Book a call",
    },
  },
  footer: {
    tagline: "Bouge ton marketing",
    description:
      "Strategic marketing consultant for SMEs, start-ups and micro-businesses. Based on the French Riviera, I work in Nice, Sophia Antipolis, Cannes, Antibes, Monaco, across the Alpes-Maritimes, throughout France and internationally (remote).",
    navTitle: "Navigation",
    navAria: "Footer navigation",
    servicesTitle: "My services",
    servicesAria: "Services",
    linkedinAria: "Magali Clouzet's LinkedIn profile",
    copyright: "Magali Clouzet — Strategic marketing consultant. All rights reserved.",
    legal: "Legal notice",
    privacy: "Privacy policy",
  },
  home: {
    seo: {
      title: "Marketing Strategy Consulting & Fractional CMO French Riviera | Magali Clouzet",
      description:
        "Boost the growth of your SME or start-up on the French Riviera. Tailor-made marketing strategy consulting and part-time outsourced marketing leadership.",
    },
    hero: {
      badge: "Strategic marketing consultant — SMEs & Start-ups",
      h1: { l1: "Turn your", l2: "marketing into a real", l3a: "growth", l3b: "driver" },
      subtitle:
        "I help business leaders build their brand strategy, successfully launch their products and manage their marketing — from strategic vision to operational execution.",
      ctaPrimary: "Book a call",
      ctaSecondary: "Explore my services",
      note: "12 years of marketing experience with international companies",
      scroll: "Scroll",
      imageAlt:
        "Strategic marketing consultant for SMEs in Nice and Sophia Antipolis — roadmap, brand audit and competitive analysis",
      badges: [
        "Outsourced Marketing Management",
        "Brand Strategy & Positioning",
        "Launch Plans (Go-to-Market)",
      ],
    },
    marquee: ["Brand Strategy", "Positioning", "Launch Plans", "Strategic Marketing", "Training", "Fractional CMO", "Marketing Audit"],
    positioning: {
      tag: "My positioning",
      h2: "Turning your marketing into a growth engine",
      bold: "I don't simply sell marketing consulting.",
      promise:
        "My promise: structure your company's marketing to make it a genuine growth lever — managed, measurable, actionable.",
      cta: "Book a call (30 min)",
      pillars: [
        { title: "In-depth Diagnostic", text: "An analysis of your situation, challenges and resources to define a strategy tailored to your business." },
        { title: "A pragmatic method", text: "A structured approach, adapted to your resources and your operational reality." },
        { title: "Clearly established priorities", text: "Key levers prioritized to focus your efforts where they have the greatest impact." },
        { title: "A 90-day roadmap", text: "A concrete action plan with clear ownership and deadlines." },
        { title: "Measurable results", text: "Clear dashboards and KPIs to track progress, adjust actions and measure their impact." },
      ],
    },
    painPoints: {
      tag: "Your challenges",
      h2: "Do any of these situations sound familiar?",
      situations: [
        { bold: "You're preparing to launch a new product or service…", rest: "but you don't know where to start or which actions to prioritize to maximize your chances of success." },
        { bold: "You've developed an excellent offer…", rest: "but you struggle to reach the right customers or clearly explain what sets you apart." },
        { bold: "Your company is growing…", rest: "but your marketing lacks structure and you feel like you're taking action without a clear strategy." },
        { bold: "Your marketing team is overwhelmed…", rest: "and you lack the time to step back, set priorities or manage projects effectively." },
        { bold: "You could use a marketing director…", rest: "but hiring full-time isn't the right solution today." },
      ],
      bannerA: "If you recognized yourself in one or more of these situations, I can help you structure your strategy and turn your ambitions into a",
      bannerStrong: "concrete action plan",
      bannerCta: "Book a free diagnostic (30 min)",
    },
    offers: {
      tag: "My Offers - Consulting & Marketing Leadership",
      h2: "Three formats, one goal: your growth.",
      seeAll: "See services in detail",
      customA: "Need specific support? All my services are adaptable. I also offer bespoke projects — from strategic workshops and speaking engagements to targeted audits.",
      customLink: "Get in touch",
      customB: "to discuss your project.",
    },
    whyMe: {
      tag: "My commitments",
      h2: "Why work with me",
      subtitle: "4 commitments to your growth",
      chapters: [
        { title: "Your challenges first, my method second", text: "A tailor-made approach, centred on your goals and your reality. Every recommendation starts from your context: your customers, your resources and your constraints. If an action doesn't directly serve your growth, it has no place in the roadmap." },
        { title: "From strategy to execution: a single point of contact", text: "From the initial audit to operational management, you have one dedicated contact at every step. I handle strategy, lead workshops, coordinate partners, and manage trade-offs, through to implementation and concrete results." },
        { title: "Zero “bullshit” marketing", text: "No miracle promises, no empty announcements. I favor clear, strategic choices, explained simply, and full transparency on what's working and what needs adjustment." },
        { title: "Measured results, performance-driven marketing", text: "Every engagement begins with key performance indicators (KPIs) defined together. You get continuous visibility on the progress of actions. The goal: turn your marketing into a managed, measurable and profitable investment." },
      ],
    },
    method: {
      tag: "Building your marketing strategy: my method",
      h2: "Six steps, one clear trajectory.",
      subtitle:
        "A proven approach with SMEs, start-ups and growing companies — from strategic diagnosis to measured results.",
      steps: [
        { title: "Understanding your challenges", text: "In-depth analysis of your market, your targets and your business model. Everything starts with listening." },
        { title: "Auditing existing marketing", text: "An objective assessment: strengths, underused levers, blind spots and priority acquisition channels." },
        { title: "Structure the strategy", text: "Positioning, value proposition, distribution channels and the resources needed to execute it." },
        { title: "Prioritize the roadmap", text: "A clear action plan, focusing your resources where the impact is strongest." },
        { title: "Deploy & Execute", text: "Operational implementation, working directly with your teams and vendors." },
        { title: "Measure & Optimize ROI", text: "Regular tracking of key performance indicators (KPIs) to make the necessary trade-offs and ensure continuous improvement." },
      ],
    },
    ai: {
      tag: "Marketing & Artificial Intelligence",
      h2: "AI, when it truly moves marketing forward.",
      subtitle:
        "Neither a gimmick nor a silver bullet: I integrate artificial intelligence pragmatically, in service of your strategy. An accelerator — never a substitute.",
      uses: [
        { title: "Content", text: "Produce more. Not think less. AI speeds up production; human expertise keeps the course." },
        { title: "Analysis", text: "Your data and customer feedback finally put to work, not just stored." },
        { title: "Monitoring", text: "Detect market shifts, competitor movements, and customer trends earlier." },
        { title: "Automation", text: "Fewer repetitive tasks. More time for strategy and decisions." },
      ],
    },
    testimonials: {
      tag: "Testimonials",
      h2: "They trusted me.",
      placeholder: "Client testimonial coming soon",
    },
    articles: {
      tag: "The blog",
      h2: "Latest articles",
      seeAll: "All articles",
    },
    faq: {
      tag: "Frequently asked questions",
      h2: "Questions you might have before reaching out",
      seeAll: "See all questions",
    },
  },
  about: {
    seo: {
      title: "Magali Clouzet | Senior Marketing Consultant French Riviera",
      description:
        "Discover my background and my vision of marketing: a pragmatic, human and modern approach to help your SME take off, in Nice, Cannes & Antibes.",
    },
    breadcrumb: "About",
    tag: "About",
    h1: "Magali Clouzet, marketing and brand strategy consultant on the French Riviera",
    p1: "I support SME leaders, startup founders, and fast-growing small businesses in Nice, Antibes, Cannes, Sophia Antipolis and Monaco — as well as remotely, in France and internationally.",
    p2strong: "My expertise: turning your offer into a genuine growth driver, through a clear brand and marketing strategy.",
    p2rest: "I work on positioning, product and service launches (Go-to-Market), campaign development and marketing management — from strategic thinking to operational execution.",
    p3a: "After 12 years leading launches and brand strategies within international groups, I bring your company",
    p3strong: "senior-level expertise",
    p3b: "that is directly operational — without the structure or the cost of an in-house team. Product launches, repositioning, or structuring a growing marketing function: I adapt to your challenge, not the other way around.",
    photoAlt: "Magali Clouzet, strategic marketing consultant — professional portrait",
    badgeLabel: "Based on the French Riviera",
    badgeCities: "Nice · Sophia Antipolis · Cannes · Antibes · Monaco",
    parcours: {
      tag: "My background",
      h2: "12 years building and deploying strong brands, in France and internationally",
      paragraphs: [
        "Before becoming an independent consultant on the French Riviera, I spent 12 years within international groups, notably as a Marketing Manager.",
        "I led the launch of over 120 products across diverse, highly competitive markets, from healthcare and pharmaceuticals to toys, in France and internationally.",
        "This experience taught me one essential thing: the fundamentals of marketing are universal, but their implementation must always adapt to the reality of the company, its market and its resources.",
        "After these years in corporate roles, I chose to put this experience to work for leaders who need a senior marketing perspective, method and hindsight, without the overhead of a full-time marketing director.",
      ],
      cards: [
        { title: "12 years of expertise", text: "In marketing strategy, brand strategy and product launches." },
        { title: "120+ Products Launched", text: "Across diverse, highly competitive markets, from pharmaceuticals to toys." },
        { title: "International experience", text: "Launch management in France and the UK, coordination of European rollouts and management of international projects (USA, Japan)." },
        { title: "Bilingual French–English", text: "To lead your projects in France and internationally." },
      ],
    },
    approach: {
      tag: "My approach",
      h2: "One consultant, three convictions.",
      values: [
        { title: "Creativity", text: "Stepping off the beaten path to find the strategic angle that sets you apart, while remaining focused on your business goals and bottom line." },
        { title: "Empowerment", text: "Every strategic choice is explained, not simply delivered. My role is also to pass on knowledge, so you can decide and make trade-offs autonomously." },
        { title: "Partnership", text: "A long-term partnership built on trust and transparency. I support you at every step of the mission and remain a dedicated partner for the long haul." },
      ],
    },
    who: {
      tag: "Who for?",
      h2: "Three profiles, one standard of excellence",
      profiles: [
        { title: "Start-up Founders", text: "Validate your positioning and nail your launch. I help you structure a clear Go-To-Market strategy and build strong market presence from day one." },
        { title: "SME Leaders", text: "Your business is growing, but your marketing lacks alignment or management time? As an outsourced marketing director, I restore method and prioritize the highest-impact actions." },
        { title: "Micro-businesses & Freelancers", text: "Move past ad-hoc marketing without the commitment of a full-time hire. I bring an outside perspective, adapted tools and efficient coordination of your vendors." },
      ],
      cta: "Discover my services",
    },
    ctaBlock: {
      title: "A marketing project on the French Riviera or remotely? Let's talk.",
      textA: "An initial 30-minute call, with no commitment, to review your current setup and clarify your priorities. Write to me at",
      textB: "or book a slot that suits you directly.",
      ctaLabel: "Book a call (30 min)",
    },
  },
  servicesPage: {
    seo: {
      title: "Marketing Services: Audit, Launch & CMO | Magali Clouzet",
      description:
        "Strategic diagnostic, Go-To-Market launch plan or part-time marketing leadership. Tailor-made services for SMEs and start-ups.",
    },
    breadcrumb: "Services",
    tag: "Services",
    h1: "Strategic Marketing Consulting & Support on the French Riviera",
    accent: "Structured support for concrete results.",
    introA: "Three tailored formats for SMEs, start-ups and micro-businesses in Nice, Sophia Antipolis, Cannes, Antibes, Monaco or remotely. Every engagement delivers",
    introStrong: "clear priorities and a directly operational action plan",
    introB: "— never a theoretical report.",
    customStrong: "Need specific support?",
    customText: "All my services are adaptable. I also offer bespoke projects — from strategic workshops and speaking engagements to targeted audits.",
    customCta: "Let's talk about your project",
    customSubject: "Custom mission",
  },
  offerPage: {
    summaryTitle: "In a nutshell",
    audienceTitle: "Who is it for?",
    includedTag: "What's included",
    includedH2: "A clear scope, no surprises.",
    processTag: "The process",
    processH2: "How it works",
    deliverablesTag: "Deliverables",
    deliverablesH2: "Key Deliverables",
    othersTitle: "Explore other services",
    ctaDefaultText: "A first free, no-commitment call to check that this format fits your situation — and to leave with a clear proposal.",
    servicesBreadcrumb: "Services",
  },
  blogPage: {
    seo: {
      title: "Strategic marketing blog: SMEs, start-ups, Go-To-Market, AI",
      description:
        "Articles and advice on strategic marketing: positioning, brand strategy, Go-To-Market, outsourced marketing management and marketing AI for SMEs and start-ups.",
    },
    breadcrumb: "Blog",
    tag: "The blog",
    h1: "Strategic marketing. Zero fluff.",
    subtitle: "Methodologies, insights, and actionable advice for SME leaders, startup founders, and marketing managers.",
    searchPlaceholder: "Search articles…",
    searchAria: "Search articles",
    filterAria: "Filter by category",
    all: "All",
    noResult: "No articles match your search.",
  },
  articlePage: {
    by: "By",
    summaryTitle: "Key takeaways",
    authorRole: "Strategic marketing consultant",
    authorBio: "Strategic marketing consultant. I help SMEs, start-ups and micro-businesses turn their marketing into a growth driver.",
    authorLink: "Learn more",
    authorAlt: "Magali Clouzet, strategic marketing consultant",
    relatedTitle: "Read next",
    readArticle: "Read the article",
    backToBlog: "Back to the blog",
    breadcrumbBlog: "Blog",
  },
  contactPage: {
    seo: {
      title: "Contact & Free Strategic Call | Magali Clouzet",
      description:
        "Ready to structure your marketing? Book a free 30-min introductory call. Support on the French Riviera (Antibes, Nice, Cannes) or remotely.",
    },
    breadcrumb: "Contact",
    tag: "Contact",
    h1: "Let's talk about your project.",
    intro:
      "Tell me about your goals, challenges, and current setup. I personally reply within 48 business hours—and if I'm not the best fit, I'll gladly point you to the right contact or resource.",
    calendlyTitle: "Book a call",
    calendlySub: "30 min discovery call • Free & no obligation",
    emailTitle: "By email",
    linkedinTitle: "On LinkedIn",
    linkedinSub: "Connect with me and follow my insights",
    zoneTitle: "Service area",
    formTitle: "Request a quote",
    formSubtitle: "Fill out the fields below—your message will go directly to my inbox.",
    labelName: "Your name",
    labelCompany: "Company name",
    labelEmail: "Business email",
    labelMessage: "Tell me about your project",
    placeholderName: "First and last name",
    placeholderCompany: "Your company",
    placeholderEmail: "you@company.com",
    placeholderMessage: "Overview, main challenges, key goals, timeline…",
    submit: "Submit request",
    sending: "Sending…",
    successTitle: "Message sent!",
    successTextA: "Thank you",
    successTextB: ", your request has arrived. I personally reply within 48 business hours.",
    errorA: "Sending failed. Please try again or write to me at",
    privacyNote: "Your information is strictly used to process your request.",
    subjectPrefix: "New quote request",
    fromName: "Magali Clouzet Website",
  },
  faqPage: {
    seo: {
      title: "FAQ Marketing Consulting & Leadership French Riviera | Magali Clouzet",
      description:
        "All your questions about outsourced marketing management, diagnostics and my methods in Antibes, Sophia Antipolis, Nice and remotely.",
    },
    breadcrumb: "FAQ",
    tag: "FAQ",
    h1: "Your questions, my answers.",
    intro:
      "Everything you need to know before entrusting me with your marketing. Have a question that's not answered here? Feel free to reach out — I'll get back to you promptly.",
    ctaTitle: "A specific question?",
    ctaText: "That's what the initial call is for: a 30 minute discovery chat to discuss your needs—100% free with no obligation.",
  },
  legalPage: {
    seo: {
      title: "Legal notice",
      description: "Legal notice of the website of Magali Clouzet, strategic marketing consultant.",
    },
    breadcrumb: "Legal notice",
    h1: "Legal notice",
    sections: [
      {
        title: "Website publisher",
        paragraphs: [
          "Magali Clouzet — Sole proprietor\nStrategic marketing consultant\nSIRET: 108 359 597 00011\nBased in Antibes (06600), Alpes-Maritimes, France — full address available on request\nEmail: contact@magaliclouzet.com",
        ],
      },
      { title: "Publication director", paragraphs: ["Magali Clouzet"] },
      {
        title: "Hosting",
        paragraphs: [
          "This website is hosted by Hostinger International Ltd., 61 Lordou Vironos Street, 6023 Larnaca, Cyprus — www.hostinger.com",
        ],
      },
      {
        title: "Intellectual property",
        paragraphs: [
          "All content on this site (text, visuals, graphic identity) is the exclusive property of Magali Clouzet, unless otherwise stated. Any reproduction, even partial, is prohibited without prior written authorization.",
        ],
      },
      {
        title: "Liability",
        paragraphs: [
          "The information published on this site is provided for guidance only. Magali Clouzet cannot be held responsible for omissions, inaccuracies or failures to update. The external links provided do not engage her liability.",
        ],
      },
      { title: "Contact", paragraphs: ["For any question about this site: contact@magaliclouzet.com"] },
    ],
  },
  privacyPage: {
    seo: {
      title: "Privacy policy",
      description:
        "Privacy policy and personal data protection (GDPR) of the website of Magali Clouzet, strategic marketing consultant.",
    },
    breadcrumb: "Privacy policy",
    h1: "Privacy policy",
    updated: "Last updated: July 2026",
    sections: [
      {
        title: "Data controller",
        paragraphs: [
          "Magali Clouzet, strategic marketing consultant — contact@magaliclouzet.com. This website complies with the General Data Protection Regulation (GDPR) and the French Data Protection Act.",
        ],
      },
      {
        title: "Data collected",
        paragraphs: [
          "The contact form transmits the information you enter (name, company, email, message) via the secure Web3Forms service, which forwards it to me by email. It is used solely to answer your request and is neither sold nor used for any other purpose.",
          "When you write to me by email, I only collect the data needed to process your request: name, contact details and the content of your message.",
        ],
      },
      {
        title: "Purposes and retention period",
        paragraphs: [
          "Your data is used exclusively to answer your request and, where applicable, to prepare a commercial proposal. It is neither sold nor shared with third parties. It is kept for a maximum of 3 years after our last exchange.",
        ],
      },
      {
        title: "Cookies",
        paragraphs: [
          "This site does not use tracking or advertising cookies. Only cookies strictly necessary for technical operation may be set.",
        ],
      },
      {
        title: "Your rights",
        paragraphs: [
          "You have the right to access, rectify, erase, restrict and object to the processing of your personal data. To exercise these rights, write to contact@magaliclouzet.com. You may also contact the CNIL (www.cnil.fr) in the event of a dispute.",
        ],
      },
    ],
  },
};
